import { AnimatePresence as LazyAnimatePresence, MotionDiv, MotionImg } from '../lib/framerMotion.lazy';
import { Suspense, useContext, useEffect, useMemo, useRef, useState } from "react"
import { Helmet, HelmetProvider } from "react-helmet-async"
import { useInView } from "react-intersection-observer"
import ReactPlayer from "react-player"
import TextareaAutosize from 'react-textarea-autosize'
import { v4 as uuidv4 } from 'uuid'
import api from "../api"
import { ReactComponent as CheckIcon } from "../assets/images/icons/ic-check.svg"
import { ReactComponent as ChevronIcon } from "../assets/images/icons/ic-chevron.svg"
import { ReactComponent as CloseIcon } from "../assets/images/icons/ic-close.svg"
import { ReactComponent as DoubleCheckIcon } from "../assets/images/icons/ic-double-check.svg"
import { ReactComponent as DownloadIcon } from "../assets/images/icons/ic-download.svg"
import { ReactComponent as FileIcon } from "../assets/images/icons/ic-file.svg"
import { ReactComponent as SendIcon } from "../assets/images/icons/ic-send.svg"
import { ReactComponent as PlayIcon } from "../assets/images/illustrations/il-play-button.svg"
import { ReactComponent as DeleteIcon } from "../assets/images/icons/ic-delete.svg"
import { ReactComponent as EditIcon } from "../assets/images/icons/ic-edit.svg"
import { ReactComponent as VideoIcon } from "../assets/images/icons/ic-camera.svg"
import { ReactComponent as PhotoIcon } from "../assets/images/icons/ic-photo.svg"
import { ReactComponent as DocumentIcon } from "../assets/images/icons/ic-document.svg"

import UserPlaceholder from "../assets/images/placeholders/user-placeholder.png"
import { Each } from "../common/Each"
import MainContext from "../common/MainContext"
import useWindowDimensions from "../common/hooks/useWindowDimensions"
import Loader from "../components/Loader"
import Skeleton from "../components/Skeleton"
import Spinner from "../components/Spinner"
import { ReactComponent as SearchIcon } from "../assets/images/icons/ic-search.svg"
import WritingLoader from "../components/WritingLoader"
import Clock from "../components/animated/Clock"
import HeaderFooterLayout from "../components/layouts/HeaderFooterLayout"
import typo from "../typography.module.css"
import { capitalize, formatDateV2, formatFileSize, formatTimeV2, groupMessagesByDate, UTCtoLocal } from "../utils"
import styles from "./Chats.module.css"
import useContextMenu from "../common/hooks/useContextMenu"
import AlertDialog from '../components/dialogs/AlertDialog';
import { useWebSocket } from '../common/WebSocketContext'
import { isToday, isYesterday } from 'date-fns';
import { getLocaleISOString } from '../utils'

const Chats = () => {

    const { websocket } = useWebSocket()
    const { showMenu } = useContextMenu();
    const context = useContext(MainContext)
    const { width } = useWindowDimensions()
    const [chats, setChats] = useState([])
    const [selectedChatID, setSelectedChatID] = useState(null)
    const selectedChatIDRef = useRef(selectedChatID)
    const userRef = useRef(context.user)
    const [text, setText] = useState('')
    const [file, setFile] = useState(null)
    const [typing, setTyping] = useState(false)
    const [searchResults, setSearchResults] = useState([])
    const [searchLoading, setSearchLoading] = useState(false)
    const [searchError, setSearchError] = useState('')
    const chatsRef = useRef(chats)

    const [reconnecting, setReconnecting] = useState(false)
    const [chatLoading, setChatLoading] = useState(false)
    const [imageUrl, setImageUrl] = useState(null)
    const [fullScreenImage, setFullScreenImage] = useState(null)

    const [messageToEdit, setMessageToEdit] = useState(null)
    const [messageToDelete, setMessageToDelete] = useState(null)

    const textRef = useRef(null)
    const endRef = useRef(null);
    const fileRef = useRef(null)

    const [loadMoreRef, isInView] = useInView({
        triggerOnce: false,
        rootMargin: '0px 0px',
    })

    const [touchStart, _setTouchStart] = useState(null)
    const [touchEnd, _setTouchEnd] = useState(null)
    const touchStartRef = useRef(touchStart)
    const touchEndRef = useRef(touchEnd)

    const setTouchStart = data => {
        touchStartRef.current = data;
        _setTouchStart(data);
    }

    const setTouchEnd = data => {
        touchEndRef.current = data;
        _setTouchEnd(data);
    }

    const sendDisabled = useMemo(() => {
        if (text.trim().length === 0 && !file) {
            return true
        }
        return false
    }, [text, file])

    const messageRef = useRef(null)
    const [query, setQuery] = useState('')


    const [connectionStatusMap, setConnectionStatusMap] = useState({})
    const [typingStatusMap, setTypingStatusMap] = useState({})

    useEffect(() => {
        chatsRef.current = chats
    }, [chats])

    const sortedChats = useMemo(() => {
        if (!chats) return [];
        // Rimuovi duplicati per id
        const uniqueChats = [];
        const seen = new Set();
        for (const chat of chats) {
            if (!seen.has(chat.id)) {
                uniqueChats.push(chat);
                seen.add(chat.id);
            }
        }
        let innerChats = uniqueChats;
        if (query.trim().length !== 0) {
            innerChats = uniqueChats.filter(c => c.members.filter(m => m?.profile_role_id !== context.user?.profile_role_id)
                .some(m => `${m.name}${m.surname}`.replace(' ', '').toLowerCase().includes(query.replace(' ', '').toLowerCase()) ||
                    `${m.surname}${m.name}`.replace(' ', '').toLowerCase().includes(query.replace(' ', '').toLowerCase())))
        }
        return innerChats.sort((a, b) => {
            let localA = a.members.find(member => member.profile_role_id === context.user.profile_role_id)
            let localB = b.members.find(member => member.profile_role_id === context.user.profile_role_id)
            let unreadA = (localA?.last_read_seq ?? 0) < a.next_seq - 1
            let unreadB = (localB?.last_read_seq ?? 0) < b.next_seq - 1
            if (unreadA && !unreadB) return -1
            if (!unreadA && unreadB) return 1
            let lastMessageA = a.messages.length > 0 ? new Date(a.messages[0].created_at) : new Date(0)
            let lastMessageB = b.messages.length > 0 ? new Date(b.messages[0].created_at) : new Date(0)
            return lastMessageB - lastMessageA
        });
    }, [chats, query])

    const selectedChat = useMemo(() => {
        if (!selectedChatID) return null
        return chats.find(chat => chat.id === selectedChatID)
    }, [selectedChatID, chats])

    const chatLocal = useMemo(() => {
        if (!selectedChat) return null
        return selectedChat.members.find(member => member.profile_role_id === context.user.profile_role_id)
    }, [selectedChat])

    const chatMember = useMemo(() => {
        if (!selectedChat) return null
        return selectedChat.members.find(member => member.profile_role_id !== context.user.profile_role_id)
    }, [selectedChat])

    useEffect(() => {
        if (!selectedChat) {
            setTouchStart(null)
            setTouchEnd(null)
            window.removeEventListener("touchstart", onTouchStart)
            window.removeEventListener("touchmove", onTouchMove)
            window.removeEventListener("touchend", onTouchEnd)
            return
        }

        websocket.sendMessage({
            type: "messaging.read",
            chat_id: selectedChat.id,
            last_read_seq: selectedChat.next_seq - 1
        })
        setChats(prev => {
            return prev.map(c => {
                if (c.id === selectedChat.id) {
                    c.members = c.members.map(m => {
                        if (m.profile_role_id === context.user.profile_role_id) {
                            m.last_read_seq = selectedChat.next_seq - 1
                        }
                        return m
                    })
                    c.update_id = uuidv4()
                }
                return c
            })
        })
        context.setUnreadMessages(0)

        const options = { passive: false };
        window.addEventListener("touchstart", onTouchStart, options);
        window.addEventListener("touchmove", onTouchMove, options);
        window.addEventListener("touchend", onTouchEnd, options);
    }, [selectedChat])

    useEffect(() => {
        getChats()

    }, [])

    useEffect(() => {
        selectedChatIDRef.current = selectedChatID
    }, [selectedChatID])

    useEffect(() => {
        userRef.current = context.user
    }, [context.user])

    const getChat = async (id) => {
        try {
            let newChat = await api.get(`/chats/${id}`)
            setChats(prev => {
                if (prev.some(c => c.id === newChat.id)) {
                    return prev
                }
                return [newChat, ...prev]
            })
        }
        catch (e) {
            console.error(e)
        }
    }

    useEffect(() => {
        if (!websocket) {
            setReconnecting(true)
        }
        else {
            setReconnecting(false)

            const onMessageReceived = async (event) => {
                const message = event.detail
                if (message.type === 'presence') {
                    setConnectionStatusMap(prev => {
                        return {
                            ...prev,
                            [message.profile_role_id]: message.online
                        }
                    })
                }
                else if (message.type === 'messaging.typing') {
                    setTypingStatusMap(prev => {
                        return {
                            ...prev,
                            [message.profile_role_id]: message.typing
                        }
                    })
                }
                else if (message.type === 'messaging.message.receipt') {
                    setChats(prev => {
                        return prev.map(c => {
                            if (c.id === message.chat_id) {
                                c.messages = c.messages.map(m => {
                                    if (m.message_id === message.message_id) {
                                        m.id = message.id
                                        m.sending = false
                                    }
                                    return m
                                })
                                c.update_id = uuidv4()
                            }
                            return c
                        })
                    })
                }
                else if (message.type === 'messaging.message.update') {
                    setChats(prev => {
                        return prev.map(c => {
                            if (c.id === message.chat_id) {
                                c.messages = c.messages.map(m => {
                                    if (m.id === message.id) {
                                        m.body = message.body
                                        m.updated_at = message.updated_at
                                    }
                                    return m
                                })
                                c.update_id = uuidv4()
                            }
                            return c
                        })
                    })
                }
                else if (message.type === 'messaging.message.delete') {
                    setChats(prev => {
                        return prev.map(c => {
                            if (c.id === message.chat_id) {
                                c.messages = c.messages.map(m => {
                                    if (m.id === message.id) {
                                        m.deleted_at = message.deleted_at
                                        m.body = null
                                    }
                                    return m
                                })
                                c.update_id = uuidv4()
                            }
                            return c
                        })
                    })
                }
                else if (message.type === 'messaging.message') {
                    if (!chatsRef.current.map(c => c.id).includes(message.chat_id)) {
                        await getChat(message.chat_id)
                        return
                    }
                    setTypingStatusMap(prev => {
                        return {
                            ...prev,
                            [message.profile_role_id]: false
                        }
                    })

                    if (message.chat_id === selectedChatIDRef.current) {
                        context.setUnreadMessages((prev) => prev - 1)
                        websocket.sendMessage({
                            type: 'messaging.read',
                            chat_id: selectedChatIDRef.current,
                            last_read_seq: message.seq,
                        });
                        setChats(prev => {
                            return prev.map(c => {
                                if (c.id === selectedChatIDRef.current) {
                                    c.members = c.members.map(m => {
                                        if (m.profile_role_id === userRef.current?.profile_role_id) {
                                            m.last_read_seq = message.seq
                                        }
                                        return m
                                    })
                                    c.update_id = uuidv4()
                                }
                                if (c.id === message.chat_id && !c.messages.some(m => m.message_id === message.message_id || m.id === message.id)) {
                                    c.messages.unshift(message)
                                    c.next_seq = Math.max(c.next_seq, message.seq + 1)
                                }
                                return c
                            })
                        })
                    }
                    else {
                        setChats(prev => {
                            const localProfileRoleId = userRef.current?.profile_role_id
                            if (!localProfileRoleId) {
                                return prev
                            }
                            return prev.map(c => {
                                if (c.id === message.chat_id && !c.messages.some(m => m.message_id === message.message_id || m.id === message.id)) {
                                    c.messages.unshift(message)
                                    c.next_seq = Math.max(c.next_seq, message.seq + 1)
                                    c.update_id = uuidv4()
                                }
                                return c
                            })
                        })
                    }
                    scrollToBottom()
                }
                else if (message.type === 'messaging.read') {
                    setChats(prev => {
                        return prev.map(c => {
                            if (c.id === message.chat_id) {
                                c.members = c.members.map(m => {
                                    if (m.profile_role_id === message.profile_role_id) {
                                        m.last_read_seq = message.last_read_seq
                                    }
                                    return m
                                })
                                c.update_id = uuidv4()
                            }
                            return c
                        })
                    })
                }
            }

            const onReconnecting = () => {
                setReconnecting(true)
            }

            const onConnected = () => {
                setReconnecting(false)
            }

            window.onpopstate = (e) => {
                setSelectedChatID(null)
            }

            document.addEventListener('message-received', onMessageReceived);
            document.addEventListener('websocket-reconnecting', onReconnecting);
            document.addEventListener('websocket-connected', onConnected);


            return () => {
                // Rimuovi l'handler quando il componente viene smontato
                document.removeEventListener('message-received', onMessageReceived);
                document.removeEventListener('websocket-reconnecting', onReconnecting);
                document.removeEventListener('websocket-connected', onConnected);
            };
        }

    }, [websocket])


    const onTouchStart = (e) => {
        if (e.touches.length === 1) {
            const touch = e.touches[0];
            if (touch.clientX < 20) {
                e.preventDefault()
                setTouchEnd(null)
                setTouchStart(touch.clientX)
            }
        }
    }

    const onTouchMove = (e) => {
        if (touchStartRef.current && touchStartRef.current < 30) {
            if (e.touches.length === 1) {
                const touch = e.touches[0];
                setTouchEnd(touch.clientX)
            }
        }
    }

    const onTouchEnd = () => {
        if (!touchStartRef || !touchEndRef) return
        const distance = touchStartRef.current - touchEndRef.current
        const isRightSwipe = distance < -10
        if (isRightSwipe && distance < -(window.innerWidth * 0.6)) {
            setTouchEnd(window.innerWidth + 10)
            setTimeout(() => {
                setSelectedChatID(null)
            }, 100)
        }
        else {
            setTouchEnd(null)
            setTouchStart(null)
        }
    }

    const getChats = async () => {
        setChatLoading(true)
        try {
            let chats = await api.get("/admin/chats")
            setChats(chats)
            void import('framer-motion');
        }
        catch (e) {
            console.error(e)
        }
        setChatLoading(false)
    }


    const send = async () => {
        let created_at = new Date().toISOString().replace('Z', '')
        let message_id = uuidv4()
        if (messageToEdit) {
            websocket.sendMessage({
                type: "messaging.message.update",
                body: { text: text },
                id: messageToEdit.id,
                chat_id: selectedChat.id,
            })
            setChats(prev => {
                return prev.map(c => {
                    if (c.id === selectedChat.id) {
                        c.messages = c.messages.map(m => {
                            if (m.id === messageToEdit.id) {
                                m.body.text = text
                            }
                            return m
                        })
                        c.update_id = uuidv4()
                    }
                    return c
                })
            })
            setMessageToEdit(null)
            setText('')
            textRef.current.focus()
            return
        }
        if (!file) {
            websocket.sendMessage({
                type: "messaging.message",
                body: { text: text },
                chat_id: selectedChat.id,
                message_id: message_id,
            })

            let message = { body: { text: text }, message_id: message_id, created_at: created_at, profile_role_id: context.user.profile_role_id, sending: true, seq: selectedChat.next_seq }
            setChats(prev => {
                return prev.map(c => {
                    if (c.id === selectedChat.id) {
                        c.messages.unshift(message)
                        c.next_seq += 1
                        c.members = c.members.map(m => {
                            if (m.profile_role_id === context.user.profile_role_id) {
                                m.last_read_seq = c.next_seq - 1
                            }
                            return m
                        })
                        c.update_id = uuidv4()
                    }
                    return c
                })
            })
            setText('')
        }
        else {
            let message = {
                body: {
                    text: text,
                    medias: [
                        {
                            url: imageUrl,
                            name: file.name,
                            size: file.size
                        }
                    ]
                },
                created_at: created_at,
                message_id: message_id,
                profile_role_id: context.user.profile_role_id,
                sending: true,
                seq: selectedChat.next_seq
            }

            setChats(prev => {
                return prev.map(c => {
                    if (c.id === selectedChat.id) {
                        c.messages.unshift(message)
                        c.next_seq += 1
                        c.members = c.members.map(m => {
                            if (m.profile_role_id === context.user.profile_role_id) {
                                m.last_read_seq = c.next_seq - 1
                            }
                            return m
                        })
                        c.update_id = uuidv4()
                    }
                    return c
                })
            })
            setText('')
            setFile(null)
            setImageUrl(null)
            try {
                let uploaded = await uploadMessageFile(file, message_id)
                message.body.medias[0].url = uploaded.url
                websocket.sendMessage({
                    type: "messaging.message",
                    body: message.body,
                    chat_id: selectedChat.id,
                    message_id: message_id,
                })

            }
            catch (e) {
                console.error(e)
            }
        }
        textRef.current.focus()
        scrollToBottom()
    }

    const uploadMessageFile = async (file, upload_id) => {
        const formData = new FormData();
        formData.append("upload_id", upload_id)
        formData.append("file", file, file.name);


        return await api.post("/admin/messages/upload", formData, {
            headers: {
                "Content-Type": "multipart/form-data",
            },
        })
    }

    const handleOnChange = async (event) => {
        setText(event.target.value)
        // send writing message
        if (typing || messageToEdit) return
        websocket.sendMessage({
            type: 'messaging.typing',
            chat_id: selectedChatID,
            typing: true
        })
        setTyping(true)
    }

    const handleKeyDown = (event) => {
        if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault()
            if (!sendDisabled) {
                send()
            }
            if (textRef && textRef.current) {
                textRef.current.focus()
            }
        }
    };

    const searchRequest = useRef(0)
    const search = (value) => {
        searchRequest.current += 1
        setQuery(value)
        setSearchResults([])
        setSearchError('')
        setSearchLoading(Boolean(value.trim()))
    }

    useEffect(() => {
        const request = ++searchRequest.current
        const controller = new AbortController()
        if (!query.trim()) {
            setSearchResults([])
            setSearchLoading(false)
            setSearchError('')
            return
        }
        setSearchLoading(true)
        const timeout = setTimeout(async () => {
            try {
                const results = await api.get(`/admin/chats/search?q=${encodeURIComponent(query.replace(/\s/g, '').toLowerCase())}`, { signal: controller.signal })
                if (request !== searchRequest.current) return
                setSearchResults(results.filter(result =>
                    result.profile_role_id !== userRef.current?.profile_role_id &&
                    !chatsRef.current.some(chat => chat.type === 'direct' && chat.members.some(member => member.profile_role_id === result.profile_role_id))
                ))
            } catch (error) {
                if (!controller.signal.aborted && request === searchRequest.current) {
                    setSearchError('Impossibile cercare i contatti. Riprova tra poco.')
                }
            } finally {
                if (request === searchRequest.current && !controller.signal.aborted) setSearchLoading(false)
            }
        }, 300)
        return () => { clearTimeout(timeout); controller.abort() }
    }, [query])

    const scrollToBottom = () => {
        setTimeout(() => {
            if (endRef && endRef.current) {
                endRef.current.scrollIntoView({ behavior: "smooth", block: "start" })
            }
        }, 200)
    }

    const handleFileOnChange = (event) => {
        let file = event.target.files[0]
        setFile(file)
        setImageUrl(URL.createObjectURL(file))
        event.target.value = null
    }

    const getFileMediaType = (file) => {
        const parts = file.name.split('.');
        const extension = parts[parts.length - 1].toLowerCase();
        const imageExtensions = ['jpg', 'jpeg', 'gif', 'svg', 'webp', 'raw', 'png']
        const videoExtensions = ['mp4', 'mov', 'mkv', 'avi']
        if (imageExtensions.includes(extension)) {
            return 'image'
        }
        else if (videoExtensions.includes(extension)) {
            return 'video'
        }
        else {
            return 'file'
        }
    }

    const getUrlMediaType = (url) => {
        if (!url) return 'file'
        const parts = url.split('/');
        const fileName = parts[parts.length - 1]
        const file_parts = fileName.split('.')
        const extension = file_parts[file_parts.length - 1].toLowerCase()
        const imageExtensions = ['jpg', 'jpeg', 'gif', 'svg', 'webp', 'raw', 'png']
        const videoExtensions = ['mp4', 'mov', 'mkv', 'avi']
        if (imageExtensions.includes(extension)) {
            return 'image'
        }
        else if (videoExtensions.includes(extension)) {
            return 'video'
        }
        else {
            return 'file'
        }
    }

    const getPictureStyle = (contactType) => {
        switch (contactType) {
            case null:
                return ''
            case 'teacher':
                return styles.teacher
            case 'tutor':
                return styles.tutor
            case 'admin':
                return styles.admin
            default:
                return ''
        }
    }

    useEffect(() => {
        if (chatMember) {
            websocket.sendMessage({
                type: "presence",
                profile_role_id: chatMember.profile_role_id
            })
        }
    }, [chatMember])

    useEffect(() => {
        if (typing) {
            setTimeout(() => {
                setTyping(false)
                websocket.sendMessage({
                    type: 'messaging.typing',
                    chat_id: selectedChat.id,
                    typing: false
                })
            }, 2000)
        }
    }, [typing])

    const [messagePage, setMessagePage] = useState(1)
    useEffect(() => {
        const loadMoreMessages = async (page = 1, per = 25) => {
            try {
                let messages = await api.get(`/admin/chats/${selectedChat.id}/messages?&page=${page}&per=${per}`)
                setChats(prev => {
                    return prev.map(c => {
                        if (c.id === selectedChat.id) {
                            c.messages = [...c.messages, ...messages]
                            c.update_id = uuidv4()
                        }
                        return c
                    })
                })

                setMessagePage(page + 1)
            }
            catch (e) {
                console.error(e)
            }
        }

        if (isInView && selectedChat) {
            if (selectedChat.messages.length < selectedChat.messages_count) {
                loadMoreMessages(messagePage, 25)
            }
        }
    }, [isInView])


    const createChat = async (profile_role_id) => {
        try {
            let newChat = await api.post("/admin/chats", { members: [profile_role_id] })
            setChats(prev => [newChat, ...prev])
            setSelectedChatID(newChat.id)
        }
        catch (e) {
            console.error(e)
        }
    }

    useEffect(() => {
        if (messageToEdit) {
            setText(messageToEdit.body.text)
            messageRef.current.focus()
        }
    }, [messageToEdit])

    useEffect(() => {
        if (!selectedChat) return;

        const local = selectedChat.members.find(
            m => m.profile_role_id === context.user.profile_role_id
        );
        const expectedSeq = selectedChat.next_seq - 1;

        if ((local?.last_read_seq ?? 0) >= expectedSeq) {
            return; // già allineato, niente messaggio → niente loop
        }

        websocket.sendMessage({
            type: 'messaging.read',
            chat_id: selectedChat.id,
            last_read_seq: expectedSeq,
        });

        setChats(prev =>
            prev.map(chat =>
                chat.id === selectedChat.id
                    ? {
                        ...chat,
                        update_id: uuidv4(),
                        members: chat.members.map(member =>
                            member.profile_role_id === context.user.profile_role_id
                                ? { ...member, last_read_seq: expectedSeq }
                                : member
                        ),
                    }
                    : chat
            )
        );
    }, [selectedChat]);

    const getLastMessageLabel = (message) => {
        if (!message) return ''
        if (message.deleted_at) return <i>Messaggio eliminato</i>
        if (message.body?.text) return message.body.text
        if (message.body?.medias && message.body.medias.length > 0) {
            const mediaType = getUrlMediaType(message.body.medias[0].url)
            if (mediaType === 'image') {
                return <i><PhotoIcon className={styles.lastMessageIcon} /> Foto</i>
            }
            else if (mediaType === 'video') {
                return <i><VideoIcon className={styles.lastMessageIcon} /> Video</i>
            }
            else {
                return <i><DocumentIcon className={styles.lastMessageIcon} /> File</i>
            }
        }
    }

    return (
        <HeaderFooterLayout hideFooter style={{ minHeight: '100dvh', backgroundColor: 'var(--background-secondary-color)' }}>
            <HelmetProvider><Helmet>
                <title>Messaggi{context.unreadMessages > 0 ? ` (${context.unreadMessages})` : ''}</title>
            </Helmet></HelmetProvider>
            <div className={styles.container}>
                <div className={styles.contacts}>
                    <div className={styles.contactsHeader}>
                        <h1 className={styles.pageTitle}>Messaggi</h1>
                        <p className={styles.pageDescription}>Le tue conversazioni</p>
                    </div>
                    <div className={styles.search}>
                        <div className={styles.searchField}><SearchIcon aria-hidden="true" /><input type="search" aria-label="Cerca una conversazione" placeholder="Cerca persone o conversazioni" value={query} onChange={event => search(event.target.value)} />{query && <button type="button" aria-label="Cancella ricerca" onClick={() => search('')}><CloseIcon aria-hidden="true" /></button>}</div>
                    </div>
                    <div className={styles.contactList}>
                        {searchLoading && <div className={styles.searchStatus} role="status"><Spinner /> Ricerca contatti…</div>}
                        {searchError && <div className={styles.searchStatus} role="alert">{searchError}</div>}
                        {query.trim() && !searchLoading && !searchError && !chatLoading && searchResults.length === 0 && sortedChats.length === 0 && <div className={styles.searchStatus} role="status">Nessun contatto trovato.</div>}
                        {searchResults.length > 0 && <div className={styles.searchLabel}>Nuove conversazioni</div>}
                        {chatLoading &&
                            <Each of={[0, 1, 2]} render={() => {
                                return (
                                    <div className={styles.skeletonContact}>
                                        <Skeleton type="circle" width="46px" height="46px" />
                                        <div className={styles.skeletonContactInfo}>
                                            <Skeleton type="rect" borderRadius="12px" width="200px" height="10px" />
                                            <Skeleton type="rect" borderRadius="8px" width="150px" height="10px" />
                                        </div>
                                    </div>
                                )
                            }} />
                        }
                        {searchResults.length > 0 &&
                            <Each of={searchResults} render={(result) => {
                                let contactType = 'student'
                                if (result.role === 'tutor') {
                                    contactType = 'tutor'
                                }
                                else if (result.role === 'teacher') {
                                    contactType = 'teacher'
                                }
                                else if (result.role && result.role !== 'student' && result.role !== 'user') {
                                    contactType = 'admin'
                                }
                                return (
                                    <div className={`${styles.contact}`}
                                        onClick={async () => {
                                            await createChat(result.profile_role_id)
                                            setQuery('')
                                            setSearchResults([])
                                        }}>
                                        <img src={result.picture ?? UserPlaceholder} alt="" className={`${styles.picture} ${getPictureStyle(contactType)}`} />
                                        <div className={styles.contactInfo}>
                                            <div className={styles.contactHeading}><div className={styles.contactName}>{result.name} {result.surname}</div><span className={`${styles.contactRole} ${styles[contactType + 'Role']}`}>{contactType === 'tutor' ? 'Tutor' : contactType === 'teacher' ? 'Docente' : contactType === 'admin' ? 'Admin' : 'Studente'}</span></div><div className={styles.contactLastMessage}>Avvia una conversazione</div>
                                        </div>
                                    </div>
                                )
                            }} />
                        }
                        <Each of={sortedChats} render={(chat) => {
                            let contact = chat.members.find(member => member.profile_role_id !== context.user?.profile_role_id)
                            let contactType = 'student'
                            let local = chat.members.find(member => member.profile_role_id === context.user?.profile_role_id)
                            const lastMessage = [...chat.messages].sort((a, b) => b.seq - a.seq)[0]
                            const unreadCount = Math.max(0, (chat.next_seq - 1) - (local?.last_read_seq ?? 0))
                            if (contact.role === 'tutor') {
                                contactType = 'tutor'
                            }
                            else if (contact.role === 'teacher') {
                                contactType = 'teacher'
                            }
                            else if (contact.role && contact.role !== 'student' && contact.role !== 'user') {
                                contactType = 'admin'
                            }
                            return (
                                <button type="button" aria-pressed={selectedChat?.id === chat.id} className={`${styles.contact} ${unreadCount > 0 ? styles.unreadContact : ''} ${selectedChat?.id === chat.id ? styles.active : ''}`}
                                    onClick={() => {
                                        setSelectedChatID(chat.id)
                                    }}>
                                    <img src={contact.picture ?? UserPlaceholder} alt="" className={`${styles.picture} ${getPictureStyle(contactType)}`} />
                                    <div className={styles.contactInfo}>
                                        <div className={styles.contactHeading}><div className={styles.contactName}>{contact.name} {contact.surname}</div><span className={`${styles.contactRole} ${styles[contactType + 'Role']}`}>{contactType === 'tutor' ? 'Tutor' : contactType === 'teacher' ? 'Docente' : contactType === 'admin' ? 'Admin' : 'Studente'}</span></div>
                                        {chat.messages.length > 0 &&
                                            <div className={styles.contactLastMessage}
                                                >
                                                {typingStatusMap[contact.profile_role_id] ? 'Sta scrivendo...' : getLastMessageLabel(lastMessage)}
                                            </div>
                                        }
                                    </div>
                                    <div className={styles.contactMessageInfo}>
                                        {unreadCount > 0 &&
                                            <div className={styles.unreadBadge}>
                                                {unreadCount}
                                            </div>
                                        }
                                        {chat.messages.length > 0 &&
                                            <div className={styles.lastMessageTime} >

                                                {isToday(UTCtoLocal(lastMessage.created_at)) && formatTimeV2(lastMessage.created_at)}

                                                {isYesterday(UTCtoLocal(lastMessage.created_at)) && formatDateV2(lastMessage.created_at)}

                                                {!isToday(UTCtoLocal(lastMessage.created_at)) && !isYesterday(UTCtoLocal(lastMessage.created_at)) &&
                                                    formatDateV2(lastMessage.created_at, { day: '2-digit', month: '2-digit', year: '2-digit' })
                                                }

                                            </div>
                                        }
                                    </div>
                                </button>
                            )
                        }} />
                    </div>
                </div>

                <Suspense fallback={null}>
                    <LazyAnimatePresence>
                        {selectedChat &&
                            <MotionDiv
                                key={`chat-${selectedChat.id}`}
                                initial={{ x: '100%' }}
                                animate={{ x: 0 }}
                                exit={{ x: '100%', transition: { duration: .2 } }}
                                transition={{ duration: .2 }}
                                style={{ left: width <= 768 ? (touchEndRef.current ?? 0) + 12 : 0 }}
                                className={`${styles.chatContainer} ${width <= 768 && selectedChat ? styles.visible : ''} ${!touchEndRef.current ? styles.leftAnimate : ''}`}>

                                {/* HEADER */}
                                <div
                                    className={styles.header}>
                                    <ChevronIcon className={styles.backToChat} onClick={() => { setSelectedChatID(null) }} />
                                    <img src={chatMember.picture ?? UserPlaceholder} alt="" className={styles.headerPicture} />
                                    <div className={styles.headerInfo}>
                                        <div className={styles.headerName}>
                                            <div>{chatMember.name} {chatMember.surname}</div>
                                        </div>
                                        {/* <div className={[typo.caption, styles.contactModules].join(' ')}>{selectedChat.courses ? `${selectedChat.courses.join(', ')}` : ''}</div> */}
                                        <div className={styles.headerAvailable}>
                                            {connectionStatusMap[chatMember.profile_role_id] &&
                                                <div className={styles.available} />
                                            }
                                            {connectionStatusMap[chatMember.profile_role_id] === false &&
                                                <div className={styles.notAvailable} />
                                            }
                                            <span className={`${styles.contactRole} ${styles[(chatMember.role === 'tutor' ? 'tutor' : chatMember.role === 'teacher' ? 'teacher' : chatMember.role && !['student', 'user'].includes(chatMember.role) ? 'admin' : 'student') + 'Role']}`}>{chatMember.role === 'tutor' ? 'Tutor' : chatMember.role === 'teacher' ? 'Docente' : chatMember.role && !['student', 'user'].includes(chatMember.role) ? 'Admin' : 'Studente'}</span> · {connectionStatusMap[chatMember.profile_role_id] ? 'Online' : 'Offline'}
                                        </div>

                                    </div>

                                    {reconnecting &&
                                        <div className={styles.reconnecting}>
                                            <Spinner />
                                            Riconnessione
                                        </div>
                                    }
                                </div>

                                {/* CHAT */}
                                <div className={styles.chat} ref={messageRef}>
                                    <div id="end" ref={endRef} />
                                    <LazyAnimatePresence>
                                        {typingStatusMap[chatMember.profile_role_id] &&
                                            <MotionDiv
                                                initial={{ y: '200%' }}
                                                animate={{ y: 0 }}
                                                transition={{ duration: .2, ease: 'easeInOut' }}
                                                exit={{ y: '200%', transition: { duration: .2, ease: 'easeInOut' } }}
                                                className={`${styles.messageContainer} ${styles.received}`}>
                                                <div className={`${styles.message} ${styles.received}`}>
                                                    <WritingLoader />
                                                </div>
                                                <div className={styles.messageTime}>Sta scrivendo..</div>
                                            </MotionDiv>
                                        }
                                    </LazyAnimatePresence>

                                    {Object.keys(groupMessagesByDate(selectedChat.messages, { reverse: true })).map((key, index) => {
                                        return (
                                            <div key={key}>
                                                <div className={styles.messageDay}>
                                                    {capitalize(formatDateV2(key))}
                                                </div>
                                                <Each of={groupMessagesByDate(selectedChat.messages)[key]} render={(message, index) => {
                                                    const prev = groupMessagesByDate(selectedChat.messages)[key][index - 1];
                                                    const next = groupMessagesByDate(selectedChat.messages)[key][index + 1];
                                                    const isLastOfGroup = !prev || prev.profile_role_id !== message.profile_role_id;
                                                    const isFirstOfGroup = !next || next.profile_role_id !== message.profile_role_id;
                                                    const messageType = message?.profile_role_id === chatLocal?.profile_role_id ? 'sent' : 'received';
                                                    // verifico che tutti i membri della chat abbiano la last_read_seq >= message.seq
                                                    const allRead = selectedChat.members.every(member => {
                                                        if (member.profile_role_id === chatLocal?.profile_role_id) return true
                                                        return member.last_read_seq >= message.seq
                                                    })
                                                    return (
                                                        <div
                                                            id={`message-${message.id}`}
                                                            className={`${styles.messageContainer} ${messageType === 'sent' ? styles.sent : styles.received}`}>
                                                            <div className={`${styles.message} ${messageType === 'sent' ? styles.sent : styles.received} ${isFirstOfGroup ? styles.first : ''} ${isLastOfGroup ? styles.last : ''}`}>
                                                                {message.body?.medias?.length > 0 &&
                                                                    <>
                                                                        {getUrlMediaType(message.body.medias[0].name) === 'image' && (
                                                                            <Suspense fallback={<img src={message.body.medias[0].url} alt="" className={styles.messageImage} onClick={() => { setFullScreenImage(message.body?.medias?.[0]?.url) }} />}>
                                                                                <ImageLoader src={message.body.medias[0].url} onClick={() => {
                                                                                    setFullScreenImage(message.body.medias[0].url)
                                                                                }} />
                                                                            </Suspense>
                                                                        )}
                                                                        {getUrlMediaType(message.body.medias[0].name) === 'video' &&
                                                                            <div className={styles.playerWrapper} style={{ maxWidth: '350px' }}>
                                                                                <ReactPlayer
                                                                                    url={message.body.medias[0].url}
                                                                                    controls
                                                                                    width="100%"
                                                                                    height="100%"
                                                                                    className={styles.player}
                                                                                    playIcon={<PlayIcon />}
                                                                                    light={false}
                                                                                    config={
                                                                                        {
                                                                                            youtube: {
                                                                                                playerVars: { showinfo: 0, modestbranding: 1, fs: 0, autoplay: 1 }
                                                                                            }
                                                                                        }
                                                                                    }
                                                                                />
                                                                            </div>
                                                                        }
                                                                        {getUrlMediaType(message.body.medias[0].name) === 'file' &&
                                                                            <div className={styles.file}>
                                                                                <div className={styles.fileInfo}>
                                                                                    <div className={styles.fileName}>
                                                                                        {message.body.medias[0].name}
                                                                                    </div>
                                                                                    <div className={styles.fileSize}>
                                                                                        {formatFileSize(message.body.medias[0].size)}
                                                                                    </div>
                                                                                </div>
                                                                                <button className={styles.downloadFile} onClick={async () => {
                                                                                    const link = document.createElement("a");
                                                                                    link.href = message.body.medias[0].url;
                                                                                    link.download = message.body.medias[0].name;
                                                                                    document.body.appendChild(link);
                                                                                    // Simula il clic sul link
                                                                                    link.click();
                                                                                    // Rimuovi il link dal corpo del documento
                                                                                    document.body.removeChild(link);
                                                                                }}>
                                                                                    <DownloadIcon className={styles.downloadIcon}></DownloadIcon>
                                                                                </button>
                                                                            </div>
                                                                        }
                                                                    </>
                                                                }

                                                                <div className={styles.messageBody}
                                                                    onContextMenu={(e) => {
                                                                        if (messageType === 'sent' && !message.sending && message.id !== null && !message.deleted_at) {
                                                                            showMenu(e, [
                                                                                {
                                                                                    name: "Modifica",
                                                                                    icon: <EditIcon style={{ color: "var(--sf-green)" }} />,
                                                                                    onClick: () => { setMessageToEdit(message) },
                                                                                },
                                                                                {
                                                                                    name: "Elimina",
                                                                                    icon: <DeleteIcon style={{ color: "var(--sf-red)" }} />,
                                                                                    onClick: () => {
                                                                                        setMessageToDelete(message)
                                                                                    },
                                                                                },
                                                                            ]);
                                                                        }
                                                                    }}
                                                                >
                                                                    <div className={styles.messageText}>
                                                                        <span className={`${styles.messageCopy} ${message.deleted_at ? styles.deletedMessage : ''}`}>{message.deleted_at ? 'messaggio eliminato' : message.body?.text ?? ''}</span>
                                                                        <div className={styles.messageTime}>
                                                                            {message.updated_at && !message.deleted_at ? "Modificato · " : ""}{formatTimeV2(message.created_at)}
                                                                            {messageType === "sent" && message.sending && (
                                                                                <Suspense fallback={null}>
                                                                                    <Clock className={styles.clock} />
                                                                                </Suspense>
                                                                            )}
                                                                            {messageType === "sent" && !message.sending && !allRead &&
                                                                                <CheckIcon className={styles.receiptIcon} />
                                                                            }
                                                                            {messageType === "sent" && !message.sending && allRead &&
                                                                                <DoubleCheckIcon className={styles.receiptIcon} />
                                                                            }
                                                                        </div>
                                                                    </div>
                                                                </div>

                                                            </div>

                                                        </div>
                                                    )
                                                }} />
                                            </div>
                                        )
                                    })
                                    }
                                    <div id="load-more" className={styles.loadMore} ref={loadMoreRef}></div>
                                    {selectedChat.messages.length < selectedChat.messages_count &&
                                        <div style={{ display: 'flex', flexDirection: 'row', width: '100%', alignItems: 'center', justifyContent: 'center', padding: '.5rem' }}>
                                            <Spinner color="black" />
                                        </div>
                                    }
                                </div>

                                {/* BOTTOM BAR */}
                                {!selectedChat.locked &&
                                    <div
                                        className={styles.bottomBar}>
                                        {file &&
                                            <div className={styles.innerFile}>

                                                {getFileMediaType(file) === 'image' &&
                                                    <>
                                                        <button className={styles.closeFile} onClick={() => { setFile(null) }}>
                                                            <CloseIcon style={{ width: '16px', height: '16px', minHeight: '16px', minWidth: '16px' }} />
                                                        </button>
                                                        <img src={imageUrl} alt="" className={styles.fileImage} />
                                                    </>
                                                }

                                                {getFileMediaType(file) === 'video' &&
                                                    <>
                                                        <button className={styles.closeFile} onClick={() => { setFile(null) }}>
                                                            <CloseIcon style={{ width: '16px', height: '16px', minHeight: '16px', minWidth: '16px' }} />
                                                        </button>
                                                        <div className={styles.playerWrapper}>
                                                            <ReactPlayer
                                                                url={imageUrl}
                                                                controls
                                                                width="100%"
                                                                height="100%"
                                                                className={styles.player}
                                                                playIcon={<PlayIcon />}
                                                                light={false}
                                                                config={
                                                                    {
                                                                        youtube: {
                                                                            playerVars: { showinfo: 0, modestbranding: 1, fs: 0, autoplay: 1 }
                                                                        }
                                                                    }
                                                                }
                                                            />
                                                        </div>
                                                    </>
                                                }

                                                {getFileMediaType(file) === 'file' &&
                                                    <div className={styles.file}>
                                                        <div className={styles.fileInfo}>
                                                            <div className={styles.fileName}>
                                                                {file.name}
                                                            </div>
                                                            <div className={styles.fileSize}>
                                                                {formatFileSize(file.size)}
                                                            </div>
                                                        </div>
                                                        <button className={styles.deleteFile} onClick={() => {
                                                            setFile(null)
                                                        }}>
                                                            <CloseIcon className={styles.deleteIcon}></CloseIcon>
                                                        </button>
                                                    </div>
                                                }
                                            </div>
                                        }
                                        {messageToEdit &&
                                            <div className={styles.innerFile} style={{ padding: '1rem' }}>
                                                <div className={typo.caption}>MODIFICA</div>
                                                <span style={{ opacity: 0.6 }}>{messageToEdit.body.text}</span>
                                                <button className={styles.closeFile} onClick={() => {
                                                    setMessageToEdit(null)
                                                    setText('')
                                                }}>
                                                    <CloseIcon style={{ width: '16px', height: '16px', minHeight: '16px', minWidth: '16px' }} />
                                                </button>
                                            </div>
                                        }
                                        <div className={styles.inner}>
                                            <input type='file' id='file' ref={fileRef} style={{ display: 'none' }} onChange={handleFileOnChange} multiple={false} />
                                            <button aria-label="Allega un file" className={styles.fileButton} onClick={() => {
                                                if (fileRef && fileRef.current) {
                                                    fileRef.current.click()
                                                }
                                            }}>
                                                <FileIcon />
                                            </button>
                                            <TextareaAutosize ref={textRef} value={text} minRows={1} maxRows={5} type="text" className={styles.messageInput} aria-label="Messaggio" placeholder="Scrivi un messaggio…" onChange={handleOnChange} onKeyDown={handleKeyDown} />
                                            <button aria-label="Invia messaggio" className={styles.send} onClick={send} disabled={sendDisabled}>
                                                <SendIcon style={{ color: 'white' }} />
                                            </button>
                                        </div>
                                    </div>
                                }
                                {selectedChat.locked &&
                                    <div className={styles.bottomBar}>
                                        <div className={typo.headline}>
                                            Tutti i corsi associati a questo docente o tutor sono scaduti, pertanto avrai accesso alle conversazioni precedenti senza la possibilità di inviare nuovi messaggi.
                                        </div>
                                    </div>
                                }
                                {fullScreenImage &&
                                    <FullScreenImage src={fullScreenImage} onClose={() => { setFullScreenImage(null) }} />
                                }


                            </MotionDiv>
                        }

                    </LazyAnimatePresence>
                </Suspense>
                {!selectedChat &&
                    <div className={styles.emptyChat}>
                        <div className={styles.emptyChatIcon} aria-hidden="true"><SendIcon /></div>
                        <h2>Le tue conversazioni, in un unico posto</h2>
                        <p>Seleziona una chat per leggere i messaggi o cerca una persona per avviare una conversazione.</p>
                    </div>
                }

            </div>
            <AlertDialog open={messageToDelete} title='Eliminare il messaggio?' text={`Sei sicuro di voler eliminare questo messaggio ? L'operazione non può essere annullata.`} actions={[

                {
                    label: 'ANNULLA',
                    onClick: () => {
                        setMessageToDelete(null)
                    }
                },
                {
                    label: 'ELIMINA',
                    accentColor: 'var(--sf-red)',
                    onClick: () => {
                        websocket.sendMessage({
                            type: "messaging.message.delete",
                            id: messageToDelete.id,
                            chat_id: selectedChat.id,
                        })
                        setChats(prev => {
                            return prev.map(c => {
                                if (c.id === selectedChat.id) {
                                    c.messages = c.messages.map(m => {
                                        if (m.id === messageToDelete.id) {
                                            m.deleted_at = new Date().toISOString()
                                            m.body = null
                                        }
                                        return m
                                    })
                                }
                                return c
                            })
                        })
                        setMessageToDelete(null)
                    }
                }
            ]} />
        </HeaderFooterLayout>
    )
}

function ImageLoader({ src, onClick }) {
    const [isLoading, setIsLoading] = useState(false);

    const handleImageLoaded = () => {
        setIsLoading(false); // Imposta lo stato su false quando l'immagine è stata caricata
    };

    const handleLoadStart = () => {
        setIsLoading(true)
    }

    return (
        <>
            {isLoading &&
                <div className={styles.imageLoader}>
                    <Loader />
                </div>
            }
            <MotionImg layoutId={src} src={src} alt="" className={styles.messageImage} onLoadStart={handleLoadStart} onLoad={handleImageLoaded} onClick={onClick} />
        </>
    );
}

function FullScreenImage({ src, onClose }) {
    return (
        <div className={styles.fullScreenImageContainer}>
            <button className={styles.closeFile} onClick={onClose}>
                <CloseIcon style={{ width: '16px', height: '16px', minHeight: '16px', minWidth: '16px' }} />
            </button>
            <Suspense fallback={null}>
                <LazyAnimatePresence>
                    <MotionImg src={src} alt="" className={styles.fullScreenImage} layoutId={src} />
                </LazyAnimatePresence>
            </Suspense>
        </div>
    )
}

export default Chats
